import React, { useState, useEffect, useContext } from 'react';
import { WalletContext } from '../context/WalletContext';
import WalletPickerModal from './WalletPickerModal';
import { recordConversion } from '../utils/sieAttribution';

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

const TOKEN_KEY = 'xyr_dashboard_token';
const GOOGLE_CLIENT_ID = '546662672164-a9teg2dop2squsactknv9h1nobm203gl.apps.googleusercontent.com';

const PARTICLES = [
  { left: '8%',  dur: '14s', del: '-2s' },
  { left: '22%', dur: '20s', del: '-6s' },
  { left: '45%', dur: '15s', del: '-1s' },
  { left: '68%', dur: '18s', del: '-8s' },
  { left: '85%', dur: '22s', del: '-4s' },
];

const CORNERS = [
  { top: 10, left: 10,  borderTop:    '2px solid rgba(160,160,160,0.3)', borderLeft:  '2px solid rgba(160,160,160,0.3)' },
  { top: 10, right: 10, borderTop:    '2px solid rgba(160,160,160,0.3)', borderRight: '2px solid rgba(160,160,160,0.3)' },
  { bottom: 10, left: 10,  borderBottom: '2px solid rgba(160,160,160,0.3)', borderLeft:  '2px solid rgba(160,160,160,0.3)' },
  { bottom: 10, right: 10, borderBottom: '2px solid rgba(160,160,160,0.3)', borderRight: '2px solid rgba(160,160,160,0.3)' },
];

export default function DashboardGate({ children }) {
  const { connected, address, signer, connectWallet } = useContext(WalletContext);
  const [status,   setStatus]   = useState('checking');
  const [authMode, setAuthMode] = useState('wallet');
  const [emailTab, setEmailTab] = useState('login');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [busy,     setBusy]     = useState(false);
  const [errMsg,   setErrMsg]   = useState('');
  const [gsiReady, setGsiReady] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    const hashMatch = window.location.hash.match(/[#&]t=([^&]+)/);
    if (hashMatch) {
      try { localStorage.setItem(TOKEN_KEY, decodeURIComponent(hashMatch[1])); } catch {}
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setStatus('gate'); return; }
    fetch(`${API_BASE}/auth/check`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? setStatus('authed') : Promise.reject())
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setStatus('gate'); });
  }, []);

  useEffect(() => {
    if (status !== 'authed') return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      const msLeft = payload.exp * 1000 - Date.now();
      if (msLeft <= 0) { localStorage.removeItem(TOKEN_KEY); setStatus('gate'); return; }
      const t = setTimeout(() => { localStorage.removeItem(TOKEN_KEY); setStatus('gate'); }, msLeft);
      return () => clearTimeout(t);
    } catch { localStorage.removeItem(TOKEN_KEY); setStatus('gate'); }
  }, [status]);

  useEffect(() => {
    if (window.google?.accounts?.id) { setGsiReady(true); return; }
    if (document.getElementById('gsi-script')) return;
    const s = document.createElement('script');
    s.id  = 'gsi-script';
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => setGsiReady(true);
    document.head.appendChild(s);
  }, []);

  const saveToken = (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    setStatus('authed');
    setErrMsg('');
    // Close the social-traffic funnel: if this visitor arrived from a tracked
    // SIE link, credit the originating post. Fire-and-forget by design.
    recordConversion('signup', email.trim() || address || undefined);
  };

  const signWallet = async () => {
    if (!signer || !address) return;
    setStatus('signing');
    setErrMsg('');
    try {
      const chRes = await fetch(`${API_BASE}/auth/challenge?address=${address}`);
      const { message } = await chRes.json();
      if (!message) throw new Error('No challenge received');
      const signature = await signer.signMessage(message);
      const vRes = await fetch(`${API_BASE}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address, signature }),
      });
      const data = await vRes.json();
      if (!vRes.ok) throw new Error(data.error || 'Verification failed');
      saveToken(data.token);
    } catch (e) {
      setErrMsg(e.message || 'Sign-in failed');
      setStatus('gate');
    }
  };

  const handleGoogle = async (response) => {
    setBusy(true);
    setErrMsg('');
    try {
      const res = await fetch(`${API_BASE}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Google sign-in failed');
      saveToken(data.token);
    } catch (e) {
      setErrMsg(e.message || 'Google sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!gsiReady) return;
    window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleGoogle });
  }, [gsiReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrMsg('');
    try {
      const endpoint = emailTab === 'login' ? '/auth/login' : '/auth/register';
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      saveToken(data.token);
    } catch (e) {
      setErrMsg(e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  if (status === 'checking') return null;
  if (status === 'authed') return children;

  return (
    <div style={{
      position: 'fixed', inset: 0, overflow: 'hidden',
      background: '#080808',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'monospace',
    }}>
      <style>{`
        @keyframes dg-grid {
          from { transform: perspective(1000px) rotateX(75deg) translateY(0px) scale(1.5); }
          to   { transform: perspective(1000px) rotateX(75deg) translateY(40px) scale(1.5); }
        }
        @keyframes dg-orb {
          0%,100% { opacity:.35; transform:translate(-50%,-50%) scale(1); }
          50%     { opacity:.65; transform:translate(-50%,-50%) scale(1.12); }
        }
        @keyframes dg-particle {
          from { transform:translateY(100vh); opacity:0; }
          10%  { opacity:1; }
          90%  { opacity:1; }
          to   { transform:translateY(-100vh); opacity:0; }
        }
        .dg-panel { transform:perspective(1200px) rotateX(6deg); transition:transform 0.4s ease,box-shadow 0.4s ease; }
        .dg-panel:hover { transform:perspective(1200px) rotateX(3deg) translateY(-5px); box-shadow:0 0 80px rgba(160,160,160,0.08),0 28px 100px rgba(0,0,0,0.9) !important; }
        .dg-btn-orange:hover { box-shadow:0 0 14px rgba(247,147,26,0.4); }
        .dg-btn-teal:hover   { box-shadow:0 0 14px rgba(38,196,154,0.4); }
      `}</style>

      {/* Animated perspective grid */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        opacity: 0.5,
        animation: 'dg-grid 16s linear infinite',
      }} />

      {/* Scanlines */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'repeating-linear-gradient(to bottom, rgba(255,255,255,0.015), rgba(255,255,255,0.015) 1px, transparent 1px, transparent 4px)',
        opacity: 0.18,
      }} />

      {/* Glow orb */}
      <div style={{
        position: 'absolute', width: 420, height: 420, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(160,160,160,0.08), transparent 70%)',
        filter: 'blur(70px)',
        top: '50%', left: '50%',
        animation: 'dg-orb 7s ease-in-out infinite',
        zIndex: 0,
      }} />

      {/* Particles */}
      {PARTICLES.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', width: 1, height: 1, borderRadius: '50%',
          background: 'rgba(160,160,160,0.5)',
          left: p.left,
          animation: `dg-particle ${p.dur} linear ${p.del} infinite`,
          zIndex: 0,
        }} />
      ))}

      {/* Login panel */}
      <div className="dg-panel" style={{
        position: 'relative', zIndex: 1,
        width: 280, padding: '22px 32px',
        background: 'rgba(8,8,8,0.85)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.04)',
        borderTop: '1px solid rgba(160,160,160,0.18)',
        borderLeft: '1px solid rgba(160,160,160,0.1)',
        borderRadius: 16,
        boxShadow: '0 0 50px rgba(160,160,160,0.04), 0 18px 70px rgba(0,0,0,0.8)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        overflow: 'hidden',
      }}>

        {/* HUD corners */}
        {CORNERS.map((s, i) => (
          <div key={i} style={{ position: 'absolute', width: 24, height: 24, ...s }} />
        ))}

        {/* Logo */}
        <div style={{ marginBottom: 14, opacity: 0.9 }}>
          <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
            <polygon points="19,2 36,11 36,27 19,36 2,27 2,11" stroke="#f7931a" strokeWidth="1.5" fill="none"/>
            <polygon points="19,9 29,15 29,23 19,29 9,23 9,15" stroke="#f7931a" strokeWidth="1" fill="rgba(247,147,26,0.08)"/>
          </svg>
        </div>
        <div style={{ color: '#f7931a', fontSize: 13, letterSpacing: '4px', marginBottom: 4, textShadow: '0 0 18px rgba(247,147,26,0.45)' }}>
          BLOCKHIVE
        </div>
        <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 9, letterSpacing: '2px', marginBottom: 20, textTransform: 'uppercase' }}>
          XYR DASHBOARD
        </div>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 17, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4 }}>
          {['wallet', 'email'].map(m => (
            <button key={m} onClick={() => { setAuthMode(m); setErrMsg(''); }}
              style={{
                background: authMode === m ? 'rgba(247,147,26,0.1)' : 'transparent',
                border: 'none',
                borderRight: m === 'wallet' ? '1px solid rgba(255,255,255,0.08)' : 'none',
                color: authMode === m ? '#f7931a' : 'rgba(255,255,255,0.25)',
                fontFamily: 'monospace', fontSize: 8, letterSpacing: '2px',
                padding: '7px 22px', cursor: 'pointer',
              }}>
              {m === 'wallet' ? 'WALLET' : 'EMAIL'}
            </button>
          ))}
        </div>

        {authMode === 'wallet' ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 11, width: '100%' }}>
            {!connected ? (
              <>
                <button onClick={() => setShowPicker(true)} className="dg-btn-orange" style={btnStyle('#f7931a')}>CONNECT WALLET</button>
                <WalletPickerModal open={showPicker} onClose={() => setShowPicker(false)} onConnected={() => setShowPicker(false)} />
              </>
            ) : status === 'signing' ? (
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: '1px' }}>CHECK WALLET…</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
                <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 9, letterSpacing: '1px' }}>
                  {address?.slice(0, 6)}…{address?.slice(-4)}
                </div>
                <button onClick={signWallet} className="dg-btn-teal" style={btnStyle('#26c49a')}>SIGN TO ENTER</button>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', margin: '2px 0' }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
              <span style={{ color: 'rgba(255,255,255,0.18)', fontSize: 8, letterSpacing: '1px' }}>OR</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
            </div>

            {/* Google button */}
            <button
              onClick={() => window.google?.accounts?.id?.prompt()}
              disabled={!gsiReady}
              style={{
                ...btnStyle('#3a3a3a'),
                background: 'rgba(50,50,50,0.25)',
                color: '#666',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, width: '100%', opacity: gsiReady ? 1 : 0.4,
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="13" height="13">
                <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.7 33.3 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 2.9l5.7-5.7C34 6.6 29.3 4.8 24 4.8 12.4 4.8 3 14.2 3 25.8S12.4 46.8 24 46.8c11 0 20-8 20-20 0-1.3-.1-2.6-.4-3.8z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 2.9l5.7-5.7C34 6.1 29.3 4.3 24 4.3c-7.7 0-14.4 4.3-17.7 10.4z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.2 44 30 44 24c0-1.3-.1-2.6-.4-4z"/>
              </svg>
              SIGN IN WITH GOOGLE
            </button>
          </div>
        ) : (
          <form onSubmit={submitEmail} style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
            <div style={{ display: 'flex', gap: 0, border: '1px solid rgba(255,255,255,0.08)', marginBottom: 4, borderRadius: 4 }}>
              {['login', 'register'].map(t => (
                <button key={t} type="button" onClick={() => { setEmailTab(t); setErrMsg(''); }}
                  style={{
                    flex: 1,
                    background: emailTab === t ? 'rgba(247,147,26,0.1)' : 'transparent',
                    border: 'none',
                    borderRight: t === 'login' ? '1px solid rgba(255,255,255,0.08)' : 'none',
                    color: emailTab === t ? '#f7931a' : 'rgba(255,255,255,0.25)',
                    fontFamily: 'monospace', fontSize: 8, letterSpacing: '1.5px',
                    padding: '5px 0', cursor: 'pointer',
                  }}>
                  {t === 'login' ? 'LOGIN' : 'REGISTER'}
                </button>
              ))}
            </div>

            <input type="email" placeholder="email" value={email} onChange={e => setEmail(e.target.value)}
              required style={inputStyle} />
            <input type="password" placeholder="password (min 8 chars)" value={password}
              onChange={e => setPassword(e.target.value)} required style={inputStyle} />

            <button type="submit" disabled={busy} className="dg-btn-teal" style={btnStyle('#26c49a')}>
              {busy ? '…' : emailTab === 'login' ? 'LOGIN' : 'CREATE ACCOUNT'}
            </button>
          </form>
        )}

        {errMsg && (
          <div style={{ marginTop: 14, color: '#ff4422', fontSize: 9, letterSpacing: '0.5px', maxWidth: 280, textAlign: 'center' }}>
            {errMsg}
          </div>
        )}

        {/* HUD footer */}
        <div style={{
          marginTop: 17, display: 'flex', justifyContent: 'space-between', width: '100%',
          fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.12)',
        }}>
          <span>NODE: ACTIVE</span>
          <span>SECURE: TLS</span>
          <span>SESSION 24H</span>
        </div>
      </div>
    </div>
  );
}

function btnStyle(color) {
  return {
    background: 'transparent',
    border: `1px solid ${color}`,
    color,
    fontFamily: 'monospace',
    fontSize: 9,
    letterSpacing: '2px',
    padding: '9px 24px',
    cursor: 'pointer',
    borderRadius: 6,
    width: '100%',
    transition: 'box-shadow 0.25s ease',
  };
}

const inputStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: 'rgba(255,255,255,0.8)',
  fontFamily: 'monospace',
  fontSize: 9,
  letterSpacing: '1px',
  padding: '10px 12px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 6,
};
